export default class DependencyManager<T> {

	private readonly dependencyLists = new Map<T, T[]>();

	// This defines two lists that are *always* kept in sync.
	// The first list holds ComponentClasses that have not yet been fully registered.
	// The second list holds functions that, when called, return arrays containing all of the dependencies for the ComponentClass in the same index in the first list
	private readonly delayedRegistrations: [T[], (() => T[])[]] = [[], []];
	
	public constructor(
		private readonly performRegistration: (item: T) => void,
		private readonly isNotRegistered: (item: T) => boolean, // I know that this is gross, but it avoids creating an additional closure
		private readonly isManagedType: (item: any) => item is T
	) {}
	
	public registerItem(item: T, dependenciesProvider: (() => T | T[]) | undefined) {
		if (!this.isNotRegistered(item)) { // Yes, this is correct. It is not not registered. See the comment on the field.
			throw new Error(`Component failed to register: The JavaScript class has already been registered.`);
		}
		const processedDependenciesProvider = this.processDependenciesProvider(item, dependenciesProvider);
		if (processedDependenciesProvider === undefined) {
			this.performRegistration(item);
			this.processDelayedRegistrations();
		} else {
			const dependencies = processedDependenciesProvider();
			if (dependencies.findIndex(v => v === undefined) === -1) { // If all of the dependencies have been loaded via imports
				// This check is equivalent to checking the entire dependency tree due to how registrations are processed
				if (dependencies.findIndex(this.isNotRegistered) === -1
					|| this.isReadyForRegistration(item, dependencies)
				) {
					const totalDependencies = this.getOrCreateDependencyListForItem(item);
					this.populateDependenciesList(item, dependencies, totalDependencies);
					totalDependencies.forEach(dep => this.populateDependenciesList(dep, dependencies, this.getOrCreateDependencyListForItem(dep)));
					this.performRegistration(item);
				} else {
					this.delayedRegistrations[0].push(item);
					this.delayedRegistrations[1].push(processedDependenciesProvider);
				}
				this.processDelayedRegistrations();
			} else {
				this.delayedRegistrations[0].push(item);
				this.delayedRegistrations[1].push(processedDependenciesProvider);
			}
		}
	}

	private readonly processDependenciesProvider = (item: T, dependenciesProvider: (() => T | T[]) | undefined) => {
		let res: (() => T[]) | undefined;
		const itemPrototype = Object.getPrototypeOf(item);
		if (dependenciesProvider === undefined) {
			if (item instanceof Function && this.isManagedType(itemPrototype)) {
				res = () => [itemPrototype];
			} else {
				res = undefined;
			}
		} else {
			const dependencies = dependenciesProvider();
			if (Array.isArray(dependencies)) {
				if (item instanceof Function && this.isManagedType(itemPrototype) && dependencies.indexOf(itemPrototype) !== -1) {
					res = () => DependencyManager.copyWithPrefix((dependenciesProvider as () => T[])(), itemPrototype);
				} else {
					res = dependenciesProvider as () => T[];
				}
			} else { // We know that dependenciesProvider() will always return an instance of T at this point
				if (item instanceof Function && this.isManagedType(itemPrototype) && dependencies !== itemPrototype) {
					res = () => [dependenciesProvider() as T, itemPrototype];
				} else {
					res = () => [dependenciesProvider() as T];
				}
			}
		}
		return res;
	}

	public getOrCreateDependencyListForItem(item: T): T[] {
		let componentClassDependencies = this.dependencyLists.get(item);
		if (componentClassDependencies === undefined) {
			// Because we cannot guarantee strict import / annotation ordering, we have to assume that
			// the dependencyList might have already been created
			this.dependencyLists.set(item, componentClassDependencies = []);
		}
		return componentClassDependencies;
	}

	private processDelayedRegistrations(maximumIndex = this.delayedRegistrations[0].length, confirmedCircularDependencies: T[] = []): number {
		let totalDeleted = 0;
		let madeChanges;
		do {
			madeChanges = false;
			for (let i = 0, max = maximumIndex - totalDeleted; i < max;) {
				const item = this.delayedRegistrations[0][i]!;
				const dependenciesProvider = this.delayedRegistrations[1][i]!;
				// We only check the delayed registrations before the current one in order to resolve circular dependencies in the order that they were added
				const availableCircularDependencies = DependencyManager.copyWithSuffix(confirmedCircularDependencies, item);
				const dependencies = dependenciesProvider();
				const [totalProcessed, readyForRegistration] = this.isReadyForRegistration(item, dependencies, i, availableCircularDependencies);
				if (readyForRegistration) {
					totalDeleted += totalProcessed;
					i -= totalProcessed;
					max -= totalProcessed;
					const totalDependencies = this.getOrCreateDependencyListForItem(item);
					this.populateDependenciesList(item, dependencies, totalDependencies);
					totalDependencies.forEach(dep => this.populateDependenciesList(dep, dependencies, this.getOrCreateDependencyListForItem(dep)));
					this.performRegistration(item);
					this.delayedRegistrations[0].splice(i, 1);
					this.delayedRegistrations[1].splice(i, 1);
					madeChanges = true;
					totalDeleted++;
					max--;
				} else {
					i++;
				}
			}
		} while (madeChanges);

		return totalDeleted;
	}
	private isReadyForRegistration(
		item: T, dependencies: T[], maximumIndex = this.delayedRegistrations[0].length,
		confirmedCircularDependencies: T[] = []
	): [number, boolean] {
		let totalDeleted = 0;
		let readyForRegistration = true;
		for (let i = 0; i < dependencies.length; i++) {
			const dependency = dependencies[i];
			if (dependency === undefined) {
				readyForRegistration = false;
				break;
			} else if (this.isNotRegistered(dependency) && confirmedCircularDependencies.indexOf(dependency) === -1) {
				const dependencyIndex = this.delayedRegistrations[0].indexOf(dependency);
				//If the dependency we are currently looking for is before us in the delayedRegistrations list, we can assume that we are dealing with a circular dependency
				if (dependencyIndex !== -1 && dependencyIndex < (maximumIndex - totalDeleted)) {
					const totalProcessed = this.processDelayedRegistrations(dependencyIndex + 1,
						DependencyManager.copyWithSuffix(confirmedCircularDependencies, item));
					if (totalProcessed > 0) {
						totalDeleted += totalProcessed;
						if (this.isNotRegistered(dependency)) {
							readyForRegistration = false;
							break;
						}
					} else {
						readyForRegistration = false;
						break;
					}
				} else {
					readyForRegistration = false;
					break;
				}
			}
		}
		return [totalDeleted, readyForRegistration];
	}

	private populateDependenciesList(originalItem: T, currentDependencies: T[], flattenedDependencies: T[]) {
		for (let i = 0; i < currentDependencies.length; i++) {
			if (currentDependencies[i] !== originalItem && flattenedDependencies.indexOf(currentDependencies[i]!) === -1) {
				flattenedDependencies.push(currentDependencies[i]!);
				this.populateDependenciesList(originalItem, this.getOrCreateDependencyListForItem(currentDependencies[i]!), flattenedDependencies);
			}
		}
	}

	private static copyWithSuffix<E>(base: E[], addition: E): E[] {
		if (base.length === 0) {
			return [addition];
		} else {
			const res = base.slice();
			res.push(addition);
			return res;
		}
	}

	private static copyWithPrefix<E>(base: E[], addition: E): E[] {
		if (base.length === 0) {
			return [addition];
		} else {
			const res = base.slice();
			res.splice(0, 0, addition);
			return res;
		}
	}
}
